import { useColorScheme } from 'nativewind';
import type { ComponentProps } from 'react';
import { ActivityIndicator, View } from 'react-native';
import type { ColorValue, ViewProps } from 'react-native';

import { BaseButton } from './BaseButton';
import ThemedText from './ThemedText';
import type { TextColor, TextType } from './ThemedText';
import { useThemePalette } from '../contexts/ThemePalette';

type ButtonHierarchy = 'primary' | 'secondary' | 'tertiary' | 'padless';
type ButtonSize = 'small' | 'large';

type BaseButtonProps = ComponentProps<typeof BaseButton>;

type ButtonProps = ViewProps & {
  onPress: () => void;
  hierarchy?: ButtonHierarchy;
  size?: ButtonSize;
  text: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  // Renders as `<a href="...">` on web (see BaseButton). Use for `mailto:` / `tel:`.
  href?: string;
  accessibilityLabel?: BaseButtonProps['accessibilityLabel'];
  accessibilityHint?: BaseButtonProps['accessibilityHint'];
  accessibilityLabelledBy?: BaseButtonProps['accessibilityLabelledBy'];
};

export function BloomButton(props: ButtonProps) {
  const {
    onPress,
    hierarchy = 'primary',
    size = 'large',
    text,
    disabled = false,
    loading = false,
    href,
    style,
    className,
    testID,
    accessibilityLabel,
    accessibilityHint,
    accessibilityLabelledBy,
  } = props;

  const palette = useThemePalette();
  const { colorScheme } = useColorScheme();

  const isDisabled = disabled || loading;

  const backgroundColor: Record<ButtonHierarchy, ColorValue> = {
    primary: isDisabled ? palette.gray[10] : palette.purple[50],
    secondary: palette.surface.pure,
    tertiary: 'transparent',
    padless: 'transparent',
  };

  const textColor: Record<ButtonHierarchy, TextColor> = {
    primary: isDisabled ? 'subtle' : 'white',
    secondary: isDisabled ? 'subtle' : 'link',
    tertiary: isDisabled ? 'subtle' : 'link',
    padless: isDisabled ? 'subtle' : 'link',
  };

  const borderColor: Record<ButtonHierarchy, ColorValue> = {
    primary: 'transparent',
    secondary: isDisabled ? palette.gray[10] : palette.purple[50],
    tertiary: 'transparent',
    padless: 'transparent',
  };

  const xPadding: Record<ButtonSize, number> = {
    small: 16,
    large: 24,
  };

  const yPadding: Record<ButtonSize, number> = {
    small: 8,
    large: 16,
  };

  const font: Record<ButtonSize, TextType> = {
    small: 'buttonSmall',
    large: 'buttonDefault',
  };

  const showShadow = hierarchy === 'primary' && size === 'large' && colorScheme !== 'dark';
  const resolvedAccessibilityLabel =
    accessibilityLabel ?? (typeof text === 'string' ? text : undefined);

  const buttonStyle = [
    {
      width: '100%' as const,
      backgroundColor: backgroundColor[hierarchy],
      borderColor: borderColor[hierarchy],
      paddingVertical: yPadding[size],
      paddingHorizontal: xPadding[size],
      borderRadius: 5000,
      borderWidth: 1,
    },
    showShadow && {
      boxShadow: '0px 4px 16px 0px rgba(107, 77, 224, 0.1)',
    },
    style,
  ];

  const spinnerColor =
    hierarchy === 'primary' ? palette.surface.pure : palette.purple[50];

  return (
    <BaseButton
      onPress={onPress}
      disabled={isDisabled}
      className={className}
      style={buttonStyle}
      testID={testID}
      href={href}
      accessibilityLabel={resolvedAccessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityLabelledBy={accessibilityLabelledBy}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {loading && <ActivityIndicator size='small' color={spinnerColor} />}
        <ThemedText color={textColor[hierarchy]} type={font[size]} className='text-center'>
          {text}
        </ThemedText>
      </View>
    </BaseButton>
  );
}
